<template>
  <!-- The app rises from the bottom over the loading screen, and goes back down on logout -->
  <transition
    enter-active-class="duration-700 ease-out"
    enter-from-class="motion-safe:translate-y-full motion-reduce:opacity-0"
    leave-active-class="duration-500 ease-in"
    leave-to-class="motion-safe:translate-y-full motion-reduce:opacity-0"
    @after-enter="appShown = true"
    @after-leave="appLeft"
  >
    <div
      v-if="appVisible"
      class="relative z-10 h-full bg-linear-to-b from-primary to-primary-700 dark:from-primary-900"
    >
      <div class="side-nav-gradient flex h-full overflow-hidden">
        <!-- BEGIN: Side Menu -->
        <div
          class="transition-width @container/menu relative hidden flex-col duration-300 ease-in-out md:flex print:hidden"
          :class="{
            'w-24 lg:w-64': !collapsedMenu,
            'w-24': collapsedMenu
          }"
        >
          <SeasonalEffect />
          <div class="z-10 min-h-0 flex-1 flex-col gap-2 pt-5 md:flex">
            <div class="shrink-0 px-5" style="max-width: 250px">
              <a href="" class="flex items-center justify-center gap-2">
                <img
                  :src="brand_logo"
                  class="inline-block @3xs/menu:w-1/3"
                  alt="logo-fm"
                />
                <img
                  v-if="!collapsedMenu"
                  :src="brand_text"
                  class="hidden @3xs/menu:inline-block @3xs/menu:w-2/3"
                  alt="logo-fm"
                />
              </a>
            </div>
            <nav
              class="flex-1 overflow-x-hidden overflow-y-auto px-5 text-white"
            >
              <MainMenu :key="keyForRefreshMenu" />
            </nav>
            <div class="shrink-0 cursor-pointer pl-5 @3xs/menu:flex">
              <FontAwesomeIcon
                :icon="menuCollapseIcon"
                size="lg"
                class="text-white"
                @click="toggleCollapsed"
              />
            </div>
            <div class="shrink-0 empty:hidden">
              <ReleaseNotificationComponent />
            </div>
            <!-- Below 2xl the partner shortcuts leave the right bar for the menu -->
            <PartnerMenuLinks class="shrink-0 2xl:hidden" />
            <div
              class="flex shrink-0 justify-center border-t p-4 @3xs/menu:justify-start"
              style="max-width: 250px"
            >
              <InfoUserModalComponent />
            </div>
          </div>
        </div>
        <!-- END: Side Menu -->
        <!-- BEGIN: Content -->
        <AiAssistantContainer>
          <div
            class="bg-surface-base content @container/main z-10 flex flex-col"
            :class="{ 'rounded-r-none 2xl:rounded-r-2xl': rightSideBar }"
          >
            <TheTopBar @ask-for-refresh="incrementKeyForRefresh" />
            <div class="min-h-0 flex-1 sm:p-6">
              <RouterView :key="keyForRefresh" />
            </div>
          </div>
        </AiAssistantContainer>
        <!-- END: Content -->
        <RightMenu v-if="rightSideBar" />
        <AiAssistantLauncher
          v-if="aiAssistantEnabled"
          :beside-right-menu="rightSideBar"
        />
      </div>
    </div>
  </transition>
  <transition
    leave-active-class="duration-500 ease-out"
    leave-from-class="transform opacity-100"
    leave-to-class="transform opacity-0"
  >
    <div
      v-if="!appVisible"
      id="loading-image"
      class="fixed inset-0 flex items-center justify-center"
    >
      <!-- The box hangs under the logo without moving it: the logo stays where
           the login page shows it, and the box can grow -->
      <div class="relative">
        <img :src="brand_logo_full" width="250" />
        <div
          v-if="!loggingOut"
          class="absolute inset-x-0 top-full mt-5 flex justify-center"
        >
          <div
            class="flex flex-col items-center rounded-xl bg-white px-5 py-1.5 whitespace-nowrap"
          >
            <div
              class="grid transition-all duration-500 ease-out"
              :class="{
                'grid-rows-[1fr]': greeting,
                'grid-rows-[0fr] opacity-0': !greeting
              }"
            >
              <span
                v-if="greeting"
                class="overflow-hidden text-center text-lg font-bold"
                data-testid="greeting"
              >
                {{ greeting }}
              </span>
            </div>
            <span
              class="transition-all duration-500"
              :class="{
                'font-bold': !greeting,
                'text-sm text-gray-500': greeting
              }"
            >
              {{ loadingMessage }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </transition>
  <FormInputConfigurator />
  <NotificationsContainer />
</template>

<script>
import fm_logo from '../../../assets/images/funeral-manager-logo-only.svg'
import fm_text from '../../../assets/images/funeral-manager-text-only.svg'
import fm_logo_full from '../../../assets/images/funeral-manager-full.svg'
import funero_logo from '../../../assets/images/funero-logo-only.svg'
import funero_text from '../../../assets/images/funero-text-only.svg'
import funero_logo_full from '../../../assets/images/funero-logo-full.svg'
import funecare_logo_full from '../../../assets/images/funecare-logo-full.svg'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import Bugsnag from '@bugsnag/js'
import { setObservedUser } from '@/observability/faro'
import {
  forgetIntendedPath,
  rememberIntendedPath
} from '@/router/utils/loginRedirect'
import NotificationsContainer from '@/views/layouts/main-layout/ToastsContainer.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import FormInputConfigurator from '@c/FormInputConfigurator.vue'
import i18n from '@/i18n'
import MainMenu from '@/views/layouts/main-layout/MainMenu.vue'
import InfoUserModalComponent from '@/views/layouts/top-bar/InfoUserModalComponent.vue'
import TheTopBar from '@/views/layouts/top-bar/TheTopBar.vue'
import ReleaseNotificationComponent from '@/views/settings/versionNote/component/ReleaseNotificationComponent.vue'
import SeasonalEffect from '@/views/layouts/main-layout/SeasonalEffects/SeasonalEffect.vue'
import RightMenu from '@/views/layouts/main-layout/RightMenu.vue'
import AiAssistantContainer from '@/views/layouts/main-layout/ai-assistant/AiAssistantContainer.vue'
import AiAssistantLauncher from '@/views/layouts/main-layout/ai-assistant/AiAssistantLauncher.vue'
import PartnerMenuLinks from '@/views/layouts/main-layout/PartnerMenuLinks.vue'
import { greetingPeriod } from '@/views/layouts/main-layout/greeting'

// The app's leave lasts 500ms: in case its after-leave never comes.
const APP_LEAVE_TIMEOUT = 1000

// Fallbacks until the Lokalise keys are synced.
const GREETING_FALLBACKS = {
  morning: 'Good morning {name}',
  afternoon: 'Good afternoon {name}',
  evening: 'Good evening {name}'
}

export default {
  components: {
    AiAssistantContainer,
    AiAssistantLauncher,
    RightMenu,
    PartnerMenuLinks,
    SeasonalEffect,
    ReleaseNotificationComponent,
    NotificationsContainer,
    FormInputConfigurator,
    TheTopBar,
    InfoUserModalComponent,
    FontAwesomeIcon,
    MainMenu
  },
  data() {
    return {
      loadingMessage: '',
      greeting: '',
      // On the way to the login: the app slides down and the loading screen
      // shows the logo alone, where the login page takes it from.
      loggingOut: false,
      appShown: false,
      afterAppLeft: null,
      keyForRefresh: 0,
      keyForRefreshMenu: 0
    }
  },
  computed: {
    ...mapGetters({
      collapsedMenu: 'sideMenu/collapsedMenu',
      getMetadataLoaded: 'metadata/getMetadataLoaded',
      getInitialMetadata: 'metadata/getInitialMetadata',
      functionalities: 'metadata/getFunctionalities',
      authUser: 'auth/getUser'
    }),
    rightSideBar() {
      return (
        this.$functionality.module.has('elys') ||
        this.$functionality.module.has('enaos')
      )
    },
    aiAssistantEnabled() {
      return this.$functionality.module.has('ai_assistant')
    },
    all_is_loaded() {
      return this.authUser !== null && this.getMetadataLoaded
    },
    appVisible() {
      return this.all_is_loaded && !this.loggingOut
    },
    brand_logo() {
      if (this.$brand === 'funero') {
        return funero_logo
      }
      return fm_logo
    },
    brand_text() {
      if (this.$brand === 'funero') {
        return funero_text
      }
      return fm_text
    },
    brand_logo_full() {
      if (this.$brand === 'funero') {
        return funero_logo_full
      } else if (this.$brand === 'funecare') {
        return funecare_logo_full
      }
      return fm_logo_full
    },
    menuCollapseIcon() {
      return [
        'fal',
        this.collapsedMenu ? 'chevron-double-right' : 'chevron-double-left'
      ]
    }
  },
  mounted() {
    document.body.classList.remove('error-page')
    document.body.classList.remove('login')
    document.body.classList.add('main')
    document.querySelectorAll('#app > div').forEach((element) => {
      element.classList.remove('content')
    })
    this.loadUser()
    this.emitter.on('reload-menu', () => {
      this.keyForRefreshMenu += 1
    })
    try {
      if (/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)) {
        document.documentElement.classList.add('os-macos')
      } else if (navigator.platform.indexOf('Win') !== -1) {
        document.body.classList.add('os-windows')
      }
    } catch (_) {}
  },
  // A logout, or an expired session (401), leaves for the login page.
  beforeRouteLeave(to, _from, next) {
    if (to.name !== 'login') {
      next()
      return
    }
    this.loggingOut = true
    if (!this.appShown) {
      next()
      return
    }
    let left = false
    this.afterAppLeft = () => {
      if (!left) {
        left = true
        next()
      }
    }
    setTimeout(this.afterAppLeft, APP_LEAVE_TIMEOUT)
  },
  methods: {
    ...mapActions('sideMenu', ['toggleCollapsed']),
    ...mapActions({
      fetchMetadata: 'metadata/fetchAllMetadata',
      processGetAuthUserAction: 'auth/getAuthUser'
    }),
    ...mapMutations({
      setPermissions: 'auth/setPermissions',
      setAuthUser: 'auth/setAuthUser'
    }),
    loadUser() {
      if (!localStorage.getItem('token')) {
        this.redirectToLogin()
        return
      }
      this.loadingMessage = this.$t('global.loading', 'loading')
      this.processGetAuthUserAction()
        .then((response) => {
          forgetIntendedPath()
          this.setAuthUser(response.user)
          setObservedUser(response.user, window.location.hostname.split('.')[0])
          Bugsnag.setUser(
            response.user.id,
            response.user.email,
            response.user.full_name
          )
          const defaultView =
            response.user.settings?.default_view_preference || 'view'

          this.$store.commit('auth/setDisplayMode', defaultView)
          // localStorage.setItem(`view_pref_${response.user.id}`, defaultView)

          this.$h.setTheme(response.user.settings?.gui_settings?.theme)
          this.$h.setPattern(response.user.settings?.gui_settings?.pattern)
          this.$h.setPatternIntensity(
            response.user.settings?.gui_settings?.pattern_intensity
          )
          this.$h.setAvatarShape(
            response.user.settings?.gui_settings?.avatar_shape
          )
          this.$h.setScrollbarVisibility(
            response.user.settings?.gui_settings?.scrollbar_visibility
          )
          this.$h.setScrollbarWidth(
            response.user.settings?.gui_settings?.scrollbar_width
          )

          this.loadPermission()
        })
        .catch((_reason) => {
          this.redirectToLogin()
        })
    },
    redirectToLogin() {
      // The URL typed by the user, before a route redirect such as / → dashboard
      rememberIntendedPath(
        this.$route.redirectedFrom?.fullPath ?? this.$route.fullPath
      )
      this.$router.push({ name: 'login' })
    },
    loadPermission() {
      this.loadingMessage = this.$t('global.loading_permission', 'loading')
      this.setPermissions()
      if (this.authUser.language !== this.$i18n.locale) {
        this.loadUserLocale()
      } else {
        this.loadMetadata()
      }
    },
    loadUserLocale() {
      this.loadingMessage = this.$t('global.loading_locale', 'loading language')
      i18n.setLocale(this.authUser.language).then((_) => {
        this.loadMetadata()
      })
    },
    // Once the user's own language is loaded, so the greeting does not switch
    // language under their eyes.
    greetUser() {
      const period = greetingPeriod()
      // Without a first name, the greeting alone (« Good evening »).
      this.greeting = this.$t(
        `global.greeting_${period}`,
        { name: this.authUser.first_name ?? '' },
        GREETING_FALLBACKS[period]
      ).trim()
    },
    loadMetadata() {
      this.greetUser()
      this.loadingMessage = this.$t('global.loading_data', 'loading')
      this.fetchMetadata()
    },
    appLeft() {
      this.appShown = false
      this.afterAppLeft?.()
    },
    incrementKeyForRefresh() {
      this.keyForRefresh += 1
    }
  }
}
</script>
